import { ObjectUtils } from "src/app/core/utils/object-utils";
import { Component, ElementRef, inject, OnDestroy, OnInit } from '@angular/core';
// Third Party
import { MenuItem } from 'primeng/api';
// Rxjs
import { Observable, Subscription, forkJoin, tap } from 'rxjs';
// Services
import { AuthService } from 'src/app/auth/auth.service';
import { AsideService, IMenu } from '../aside.service';
// Model
import { Router } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { ClearLockUtilityComponent } from 'src/app/components/modals/clear-lock-utility/clear-lock-utility.component';
import { DeveloperToolsComponent } from 'src/app/components/modals/developer-tools/developer-tools.component';
import { EntityName } from 'src/app/core/models/enumerations/entity-name';
import { Role } from 'src/app/core/models/enumerations/role';
import { CellContainerService } from 'src/app/core/services/cell-container.service';
import { HeaderService } from '../../header/header.service';
import { StringUtils } from 'src/app/core/utils/string-utils';

@Component({
  selector: 'app-nav-group-item',
  standalone: false,
  templateUrl: './nav-group-item.component.html',
  styleUrls: ['./nav-group-item.component.scss']
})
export class NavGroupItemComponent implements OnInit, OnDestroy {
    private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);
   
    items: MenuItem[] = [];
    private menuChanged?: Subscription;
    private preferencesItems!: MenuItem[];
    private companyItems!: MenuItem[];
    private siteItems!: MenuItem[];
    private pipelineItems!: MenuItem[];
    private transmissionLineItems!: MenuItem[];
    private plantItems!: MenuItem[];
    private plantLtsaItems!: MenuItem[];
    private unitLtsaItems!: MenuItem[];
    private pipelineLtsaItems!: MenuItem[];
    private confirmedProjectItems!: MenuItem[];
    private unconfirmedProjectItems!: MenuItem[];
    private unitItems!: MenuItem[];
    private driveItems!: MenuItem[];
    private equipmentItems!: MenuItem[];
    private mEquipmentItems!: MenuItem[];
    private tankItems!: MenuItem[];
    private boilerItems!: MenuItem[];
    private umbrellaProjectItems!: MenuItem[];
    private areaItems!: MenuItem[];
    private home!: MenuItem[];
    private releaseItems!: MenuItem[];
    private offlineEventItems!: MenuItem[];
    private geographyItems!: MenuItem[];
    private regionAlertingItems!: MenuItem[];
    private subscriptionEditItems!: MenuItem[];
    private subscriptionCopyItems!: MenuItem[];
    private codeFileItems!: MenuItem[];

    private hasCompanyScreen: boolean = false;
    private hasSiteScreen: boolean = false;
    private hasPipelineScreen: boolean = false;
    private hasTLineScreen: boolean = false;
    private hasPlantScreen: boolean = false;
    private hasAreaScreen: boolean = false;
    private hasUnitScreen: boolean = false;
    private hasBoilerScreen: boolean = false;
    private hasDriveScreen: boolean = false;
    private hasTankScreen: boolean = false;
    private hasMEquipmentScreen: boolean = false;
    private hasOEquipmentScreen: boolean = false;
    private hasOfflineEventScreen: boolean = false;
    private hasConfirmedProjectScreen: boolean = false;
    private hasUnconfirmedProjectScreen: boolean = false;
    private hasPlantLTSAScreen: boolean = false;
    private hasUnitLTSAScreen: boolean = false;
    private hasPipelineLTSAScreen: boolean = false;
    private hasReleaseScreen: boolean = false;

    private hasAECFirmRole = false;
    private hasManufacturerRole = false;
    private hasServiceContractorRole = false;
    private hasCrudeSlateTypeRole = false;
    private hasKvRatingRole = false;
    private hasTransmissionSystemRole = false;
    private hasPipelineSystemRole = false;
    private hasDrivenEquipmentRole = false;
    private hasEquipmentTypeRole = false;
    private hasHsProductRole = false;
    private hasPipelineMeterRole = false;
    private hasManageSicProductRole = false;
    private hasUsageSicProductRole = false;

    private hasAnyCodefileRole: boolean = false;
    
    private hasRegionAlertingRole: boolean = false;
    private hasClearLockRole: boolean = false;
    private hasDevToolsRole: boolean = false;
    private hasManageGeographyRole: boolean = false;
    private hasSubscriptionRole: boolean = false;
    
    private focusedSectionSub?: Subscription;
    private currentFocusedSectionId: string | null = null;
    private static readonly FOCUS_HIGHLIGHT_CLASS = 'nav-item--focused';
    private static readonly SECTION_ID_KEY = '__sectionId';

    constructor(
                private readonly authService: AuthService,
                private readonly router: Router,
                private readonly asideService: AsideService,
                private readonly headerService: HeaderService,
                private readonly dialogService: DialogService,
                private readonly cellContainerService: CellContainerService) {
    }

    ngOnInit() {
        this.loadMenuItems();
        forkJoin([ this.loadScreens(), this.loadRoles() ]).subscribe(() => {
            // All loader functions are now complete, so we can utilize that information to set up the menu 
            this.configureMenu();
        });
        
        this.menuChanged = this.asideService.menuChanged$.subscribe(menuData => {
            this.toggleMenu(menuData);
        });
        
        this.focusedSectionSub = this.asideService.focusedSectionId$.subscribe(id => {
console.log('[nav-group-item] focused section:', id); // DJD
            this.currentFocusedSectionId = id;
            this.applyFocusHighlight();
        });  
    }

    ngOnDestroy(): void {
        this.menuChanged?.unsubscribe();
        this.focusedSectionSub?.unsubscribe();
    }

    private loadScreens(): Observable<any> {
        return forkJoin([
            this.authService.hasUserScreen(EntityName.COMPANY).pipe(tap(b => this.hasCompanyScreen = b)),
            this.authService.hasUserScreen(EntityName.SITE).pipe(tap(b => this.hasSiteScreen = b)),
            this.authService.hasUserScreen(EntityName.ASSET_PIPELINE).pipe(tap(b => this.hasPipelineScreen = b)),
            this.authService.hasUserScreen(EntityName.ASSET_TRANSMISSION_LINE).pipe(tap(b => this.hasTLineScreen = b)),
            this.authService.hasUserScreen(EntityName.PLANT).pipe(tap(b => this.hasPlantScreen = b)),
            this.authService.hasUserScreen(EntityName.AREA).pipe(tap(b => this.hasAreaScreen = b)),
            this.authService.hasUserScreen(EntityName.UNIT).pipe(tap(b => this.hasUnitScreen = b)),
            this.authService.hasUserScreen(EntityName.BOILER).pipe(tap(b => this.hasBoilerScreen = b)),
            this.authService.hasUserScreen(EntityName.TURBINE).pipe(tap(b => this.hasDriveScreen = b)),
            this.authService.hasUserScreen(EntityName.TANK).pipe(tap(b => this.hasTankScreen = b)),
            this.authService.hasUserScreen(EntityName.MEQUIPMENT).pipe(tap(b => this.hasMEquipmentScreen = b)),
            this.authService.hasUserScreen(EntityName.OEQUIPMENT).pipe(tap(b => this.hasOEquipmentScreen = b)),
            this.authService.hasUserScreen(EntityName.OFFLINE_EVENT).pipe(tap(b => this.hasOfflineEventScreen = b)),
            this.authService.hasUserScreen(EntityName.PROJECT_CONFIRMED).pipe(tap(b => this.hasConfirmedProjectScreen = b)),
            this.authService.hasUserScreen(EntityName.PROJECT_UNCONFIRMED).pipe(tap(b => this.hasUnconfirmedProjectScreen = b)),
            this.authService.hasUserScreen(EntityName.PLANT_LTSA).pipe(tap(b => this.hasPlantLTSAScreen = b)),
            this.authService.hasUserScreen(EntityName.UNIT_LTSA).pipe(tap(b => this.hasUnitLTSAScreen = b)),
            this.authService.hasUserScreen(EntityName.PIPELINE_LTSA).pipe(tap(b => this.hasPipelineLTSAScreen = b)),
            this.authService.hasUserScreen(EntityName.RELEASE).pipe(tap(b => this.hasReleaseScreen = b))
        ]);
    }

    private loadRoles(): Observable<any> {
        return forkJoin([
            this.authService.isUserInRole(Role.CLEAR_LOCKS).pipe(tap(b => this.hasClearLockRole = b)),
            this.authService.isUserInRole(Role.DEV_TOOLS).pipe(tap(b => this.hasDevToolsRole = b)),
            this.authService.isUserInRole(Role.MANAGE_GEOGRAPHY).pipe(tap(b => this.hasManageGeographyRole = b)),
            this.authService.isUserInRole(Role.EDIS_ACCESS_ADMIN).pipe(tap(b => this.hasSubscriptionRole = b)),
            this.authService.isUserInRole(Role.EDIS_ALERT_REGION).pipe(tap(b => this.hasRegionAlertingRole = b)),
            this.loadCodefileRoles()
        ]);
    }

    private loadCodefileRoles(): Observable<any> {
        return forkJoin([        
            this.authService.isUserInRole(Role.MANAGE_AEC).pipe(tap(b => this.hasAECFirmRole = b)),
            this.authService.isUserInRole(Role.MANAGE_MFG).pipe(tap(b => this.hasManufacturerRole = b)),
            this.authService.isUserInRole(Role.MANAGE_SERV_CONTR).pipe(tap(b => this.hasServiceContractorRole = b)),
            this.authService.isUserInRole(Role.MANAGE_CRUDE_SLATE).pipe(tap(b => this.hasCrudeSlateTypeRole = b)),
            this.authService.isUserInRole(Role.MANAGE_KV_RATINGS).pipe(tap(b => this.hasKvRatingRole = b)),
            this.authService.isUserInRole(Role.MANAGE_PIPELINE_SYSTEM).pipe(tap(b => this.hasPipelineSystemRole = b)),
            this.authService.isUserInRole(Role.MANAGE_DRIVEN_EQUIPMENT).pipe(tap(b => this.hasDrivenEquipmentRole = b)),
            this.authService.isUserInRole(Role.MANAGE_HS_PRODUCT).pipe(tap(b => this.hasHsProductRole = b)),
            this.authService.isUserInRole(Role.MANAGE_TRANSMISSION_SYSTEM).pipe(tap(b => this.hasTransmissionSystemRole = b)),
            this.authService.isUserInRole(Role.MANAGE_EQUIPMENT).pipe(tap(b => this.hasEquipmentTypeRole = b)),
            this.authService.isUserInRole(Role.MANAGE_PIPELINE_METER).pipe(tap(b => this.hasPipelineMeterRole = b)),
            this.authService.isUserInRole(Role.MANAGE_SIC_PRODUCTS).pipe(tap(b => this.hasManageSicProductRole = b)),
            this.authService.isUserInRole(Role.CHANGE_SIC_PRODUCTS_ED_USAGE).pipe(tap(b => this.hasUsageSicProductRole = b)),
            
        ]).pipe(tap((a: Array<boolean>) => this.hasAnyCodefileRole = a.includes(true)));
    }

    private loadMenuItems() {
        this.preferencesItems = [
            {
                label: 'Preferences Details',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Preferences', 'preferences'),
                ]
            }
        ];
        this.companyItems = [
            {
                label: 'Company Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('Details', 'details'),
                    this.buildMenuItem('Company Types', 'types'),
                    this.buildMenuItem('Greenhouse Gas', 'greenhouseGas'),
                    this.buildMenuItem('Contacts', 'contacts'),
                    this.buildMenuItem('Related Info', 'relatedInfo'),
                    this.buildMenuItem('Notes', 'notes'),
                ]
            },
        ];

        this.siteItems = [
            {
                label: 'Site Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('Contacts', 'contacts'),
                    this.buildMenuItem('Related Info', 'relatedInfo'),
                    this.buildMenuItem('Notes', 'notes'),
                ]
            }
        ];

        this.pipelineItems = [
            {
                label: 'Pipeline Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('Pipeline Path', 'path'),
                    this.buildMenuItem('Pipeline Connections', 'connections'),
                    this.buildMenuItem('Pipeline Laterals', 'laterals'),
                    this.buildMenuItem('Related Info', 'relatedInfo'),
                    this.buildMenuItem('Notes', 'notes'),
                ]
            },
        ];

        this.transmissionLineItems = [
            {
                label: 'Transmission Line Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('T-Line Path', 'path'),
                    this.buildMenuItem('T-Line Connections', 'connections'),
                    this.buildMenuItem('Related Info', 'relatedInfo'),
                    this.buildMenuItem('Notes', 'notes'),
                ]
            },
        ];

        this.plantItems = [
            {
                label: 'Plant Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('Connected Plants', 'connectedPlants'),
                    this.buildMenuItem('Ownership', 'ownership'),
                    this.buildMenuItem('Industrial Info', 'industrialInfo'),
                    this.buildMenuItem('Energy Details', 'energyDetails'),
                    this.buildMenuItem('Greenhouse Gas', 'greenhouseGas'),
                    this.buildMenuItem('Pipeline Meters', 'pipelineMeters'),
                    this.buildMenuItem('Operational Details', 'operationalDetails'),
                    this.buildMenuItem('Capacity', 'capacity'),
                    this.buildMenuItem('ISO Queues', 'isoQueue'),
                    this.buildMenuItem('Contacts', 'contacts'),
                    this.buildMenuItem('Notes', 'notes'),
                    this.buildMenuItem('Related Info', 'relatedInfo'),
                    this.buildMenuItem('Alerts', 'alerts'),
                    this.buildMenuItem('Mine Info', 'mineInfo', false),
                ]
            },
        ];

        this.plantLtsaItems = [
            {
                label: 'Plant LTSA Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('Service Agreement Types', 'serviceAgreementTypes'),
                    this.buildMenuItem('Contacts', 'contacts'),
                    this.buildMenuItem('Notes', 'notes'),
                ]
            },
        ];

        this.unitLtsaItems = [
            {
                label: 'Unit LTSA Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                ]
            },
        ];

        this.pipelineLtsaItems = [
            {
                label: 'Pipeline LTSA Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('Service Agreement Types', 'serviceAgreementTypes'),
                    this.buildMenuItem('Contacts', 'contacts'),
                ]
            },
        ];

        this.confirmedProjectItems = [
            {
                label: 'Confirmed Projects Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('Units', 'units'),
                    this.buildMenuItem('Offline Events', 'offlineEvents'),
                    this.buildMenuItem('Scope and Schedule', 'scopeAndSchedule'),
                    this.buildMenuItem('Notes', 'notes'),
                    this.buildMenuItem('Contacts', 'contacts'),
                    this.buildMenuItem('Expanded Needs', 'expandedNeeds'),
                    this.buildMenuItem('Definitive Needs', 'definitiveNeeds'),
                    this.buildMenuItem('ISO Queues', 'isoQueue'),
                    this.buildMenuItem('Related Info', 'relatedInfo'),
                    this.buildMenuItem('Pipeline Details', 'pipelineDetails', false),
                ]
            },
        ];

        this.unconfirmedProjectItems = [
            {
                label: 'Unconfirmed Projects Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('Units', 'units'),
                    this.buildMenuItem('Offline Events', 'offlineEvents'),
                    this.buildMenuItem('Scope and Schedule', 'scopeAndSchedule'),
                    this.buildMenuItem('Notes', 'notes'),
                    this.buildMenuItem('Contacts', 'contacts'),
                    this.buildMenuItem('Expanded Needs', 'expandedNeeds'),
                    this.buildMenuItem('Definitive Needs', 'definitiveNeeds'),
                    this.buildMenuItem('ISO Queues', 'isoQueue'),
                    this.buildMenuItem('Related Info', 'relatedInfo'),
                ]
            },
        ];

        this.umbrellaProjectItems = [
            {
                label: 'Umbrella Projects Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('Summary', 'summary'),
                ]
            },
        ];

        this.unitItems = [
            {
                label: 'Unit Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('Equipment', 'equipment'),
                    this.buildMenuItem('Industrial Info', 'industrialInfo'),
                    this.buildMenuItem('Contacts', 'contacts'),
                    this.buildMenuItem('Capacity', 'capacity'),
                    this.buildMenuItem('ISO Queues', 'isoQueue'),
                    this.buildMenuItem('Related Info', 'relatedInfo'),
                    this.buildMenuItem('Notes', 'notes'),
                    this.buildMenuItem('Crude Slate', 'crudeSlate', false),
                ]
            },
        ];

        this.tankItems = [
            {
                label: 'Tank Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('Related Info', 'relatedInfo'),
                ]
            },
        ];

        this.driveItems = [
            {
                label: 'Drive Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('Related Info', 'relatedInfo'),
                ]
            },
        ];

        this.boilerItems = [
            {
                label: 'Boiler Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('Related Info', 'relatedInfo'),
                ]
            },
        ];

        this.equipmentItems = [
            {
                label: 'Other Equipment Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('Related Info', 'relatedInfo'),
                    this.buildMenuItem('Notes', 'notes'),
                ]
            },
        ];

        this.mEquipmentItems = [
            {
                label: 'Mining Equipment Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('Related Info', 'relatedInfo'),
                    this.buildMenuItem('Notes', 'notes'),
                ]
            },
        ];

        this.areaItems = [
            {
                label: 'Area Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('Contacts', 'contacts'),
                    this.buildMenuItem('Related Info', 'relatedInfo'),
                    this.buildMenuItem('Notes', 'notes'),
                ]
            },
        ];

        this.releaseItems = [
            {
                label: 'Release',
                icon: 'pi pi-file-export',
                visible: true,
                expanded: true,
                items: []
            },
        ];

        this.offlineEventItems = [
            {
                label: 'Offline Event Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('Notes', 'notes'),
                    this.buildMenuItem('Equipment', 'equipment'),
                    this.buildMenuItem('Related Info', 'relatedInfo'),
                    this.buildMenuItem('Alerts', 'alerts'),
                ]
            },
        ];

        this.geographyItems = [
            {
                label: 'Geography Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                ]
            },
        ];

        this.regionAlertingItems = [
            {
                label: 'Region Alerting Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                    this.buildMenuItem('Alert History', 'alertHistory'),
                ]
            },
        ];

        this.subscriptionEditItems = [
            {
                label: 'Edit Subscription Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Edit Subscription', 'main'),
                ]
            },
        ];

        this.subscriptionCopyItems = [
            {
                label: 'Copy Subscription Input',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Copy Subscription', 'main'),
                ]
            },
        ];

        this.codeFileItems = [
            {
                label: '',
                icon: 'pi pi-list',
                visible: true,
                expanded: true,
                items: [
                    this.buildMenuItem('Main', 'main'),
                ]
            },
        ];
    }
   
   private configureMenu(): void {
        

        this.home = [
            {
                label: 'Entity Screens',
                visible: true,
                expanded: true,
                icon: 'pi pi-file-edit',
                items: [
                    {
                        label: 'Company',
                        command: (click) => {this.goTo('company', EntityName.COMPANY)},
                        visible: this.hasCompanyScreen
                    },

                    {
                        label: 'Asset',
                        visible: this.hasSiteScreen || this.hasPipelineScreen || this.hasTLineScreen,
                        items: [
                            {
                                label: 'Site',
                                command: (click) => {this.goTo('site', EntityName.SITE)},
                                styleClass: 'submenu-item',
                                visible: this.hasSiteScreen
                            },

                            {
                                label: 'Pipeline',
                                command: (click) => {this.goTo('pipeline', EntityName.ASSET_PIPELINE)},
                                styleClass: 'submenu-item',
                                visible: this.hasPipelineScreen
                            },

                            {
                                label: 'Transmission Line',
                                command: (click) => {this.goTo('transmissionLine', EntityName.ASSET_TRANSMISSION_LINE)},
                                styleClass: 'submenu-item',
                                visible: this.hasTLineScreen
                            },
                        ],
                    },
                    {
                        label: 'Plant',
                        command: (click) => {this.goTo('plant', EntityName.PLANT)},
                        visible: this.hasPlantScreen
                    },

                    {
                        label: 'Area',
                        command: (click) => {this.goTo('area', EntityName.AREA)},
                        visible: this.hasAreaScreen,
                    },

                    {
                        label: 'Unit',
                        command: (click) => {this.goTo('unit', EntityName.UNIT)},
                        visible: this.hasUnitScreen
                    },

                    {
                        label: 'Equipment',
                        visible: this.hasBoilerScreen || this.hasDriveScreen || this.hasTankScreen || this.hasMEquipmentScreen || this.hasOEquipmentScreen,
                        items: [
                            {
                                label: 'Boiler',
                                command: (click) => {this.goTo('boiler', EntityName.BOILER)},
                                styleClass: 'submenu-item',
                                visible: this.hasBoilerScreen
                            },

                            {
                                label: 'Drive',
                                command: (click) => {this.goTo('drive', EntityName.TURBINE)},
                                styleClass: 'submenu-item',
                                visible: this.hasDriveScreen
                            },

                            {
                                label: 'Tank',
                                command: (click) => {this.goTo('tank', EntityName.TANK)},
                                styleClass: 'submenu-item',
                                visible: this.hasTankScreen
                            },

                            {
                                label: 'Mining Equipment',
                                command: (click) => {this.goTo('miningEquipment', EntityName.MEQUIPMENT)},
                                styleClass: 'submenu-item',
                                visible: this.hasMEquipmentScreen
                            },

                            {
                                label: 'Other Equipment',
                                command: (click) => {this.goTo('otherEquipment', EntityName.OEQUIPMENT)},
                                styleClass: 'submenu-item',
                                visible: this.hasOEquipmentScreen
                            },
                        ],
                    },

                    {
                        label: 'Offline Event',
                        command: (click) => {this.goTo('offlineEvent', EntityName.OFFLINE_EVENT)},
                        visible: this.hasOfflineEventScreen,
                    },

                    {
                        label: 'Project',
                        visible:  this.hasConfirmedProjectScreen || this.hasUnconfirmedProjectScreen,
                        items: [
                            {
                                label: 'Confirmed Project',command: (click) => {this.goTo('confirmedProject', EntityName.PROJECT_CONFIRMED)},
                                styleClass: 'submenu-item',
                                visible: this.hasConfirmedProjectScreen
                            },

                            {
                                label: 'Unconfirmed Project',
                                command: (click) => {this.goTo('unconfirmedProject', EntityName.PROJECT_UNCONFIRMED)},
                                styleClass: 'submenu-item',
                                visible: this.hasUnconfirmedProjectScreen
                            },

                            {
                                label: 'Umbrella Project',
                                command: (click) => {this.goTo('umbrellaProject', EntityName.UMBRELLA_PROJECT)},
                                styleClass: 'submenu-item',
                                visible: this.hasConfirmedProjectScreen
                            },
                        ],
                    },

                    {
                        label: 'Agreements',
                        visible: this.hasPlantLTSAScreen || this.hasUnitLTSAScreen || this.hasPipelineLTSAScreen,
                        items: [
                            {
                                label: 'Plant LTSA',
                                command: (click) => {this.goTo('plantLtsa', EntityName.PLANT_LTSA)},
                                styleClass: 'submenu-item',
                                visible: this.hasPlantLTSAScreen
                            },

                            {
                                label: 'Unit LTSA',
                                command: (click) => {this.goTo('unitLtsa', EntityName.UNIT_LTSA)},
                                styleClass: 'submenu-item',
                                visible: this.hasUnitLTSAScreen
                            },

                            {
                                label: 'Pipeline LTSA',
                                command: (click) => {this.goTo('pipelineLtsa', EntityName.PIPELINE_LTSA)},
                                styleClass: 'submenu-item',
                                visible: this.hasPipelineLTSAScreen
                            }
                        ],
                    },

                    {
                        label: 'Release',
                        command: (click) => {this.goTo('release', EntityName.RELEASE)},
                        visible: this.hasReleaseScreen
                    },
                ]
            },

            {
                label: 'Admin',
                icon: 'pi pi-key',
                visible: this.hasClearLockRole || this.hasDevToolsRole || this.hasManageGeographyRole || this.hasSubscriptionRole || this.hasAnyCodefileRole,
                expanded: true,
                items: [
                    {
                        label: 'Geography',
                        command: (click) => {this.goTo('geography', EntityName.GEOGRAPHY)},
                        visible: this.hasManageGeographyRole
                    },
                    {
                        label: 'Code Files',
                        visible: this.hasAnyCodefileRole,
                        items: [
                            this.getMenuItem(EntityName.AEC_FIRM, this.hasAECFirmRole, true),
                            this.getMenuItem(EntityName.MANUFACTURER, this.hasManufacturerRole, true),
                            this.getMenuItem(EntityName.SERV_CONTRACTOR, this.hasServiceContractorRole, true),
                            this.getMenuItem(EntityName.CRUDE_SLATE_TYPE, this.hasCrudeSlateTypeRole, true),
                            this.getMenuItem(EntityName.KV_RATING, this.hasKvRatingRole, true, 'kV Ratings'),
                            this.getMenuItem(EntityName.PIPELINE_SYSTEM, this.hasPipelineSystemRole, true),
                            this.getMenuItem(EntityName.DRIVEN_EQUIPMENT_TYPE, this.hasDrivenEquipmentRole, true, 'Driven Equipment'),
                            this.getMenuItem(EntityName.HS_PRODUCT, this.hasHsProductRole, true, 'HS Products'),
                            this.getMenuItem(EntityName.TRANSMISSION_SYSTEM, this.hasTransmissionSystemRole, true),
                            this.getMenuItem(EntityName.EQUIPMENT_TYPE, this.hasEquipmentTypeRole, true, 'Equipment Types'),
                            this.getMenuItem(EntityName.PIPELINE_METER, this.hasPipelineMeterRole, true),
                            this.getMenuItem(EntityName.SIC_PRODUCT, this.hasManageSicProductRole || this.hasUsageSicProductRole, true),
                        ],
                    },
                    {
                        label: 'Region Alerting',
                        command: (click) => {this.goTo('regionAlerting', EntityName.REGION_ALERTING)},
                        visible: this.hasRegionAlertingRole
                    },
                    {
                        label: 'Subscription',
                        visible: this.hasSubscriptionRole,
                        items: [
                            {
                                label: 'Edit Subscription',
                                command: (click) => {this.goTo('subscription/edit', EntityName.SUBSCRIPTION_EDIT)},
                                styleClass: 'submenu-item',
                                visible: this.hasSubscriptionRole
                            },
                            {
                                label: 'Copy Subscription',
                                command: (click) => {this.goTo('subscription/copy', EntityName.SUBSCRIPTION_COPY)},
                                styleClass: 'submenu-item',
                                visible: this.hasSubscriptionRole
                            }
                        ],
                    },
                    {
                        label: 'Clear Lock Utility',
                        command: (click) => {this.openClearLockUtility()},
                        visible: this.hasClearLockRole
                    },
                    {
                        label: 'Developer Tools',
                        command: (click) => {this.openDeveloperTools()},
                        visible: this.hasDevToolsRole
                    },
                ]
            }
        ];

        if(this.items.length == 0) {
            this.items = this.home;
            this.headerService.clearReleaseSection();
        }

        const currentUrl = this.router.url;
        if(currentUrl.includes('/company')) {
            this.toggleMenu({name: 'company'});
        } else if(currentUrl.includes('/dashboard')) {
            this.toggleMenu({name: 'home'});
        } else if(currentUrl.includes('/preferences')) {
            this.toggleMenu({name: 'preferences'});
        } else if(currentUrl.includes('/site')) {
            this.toggleMenu({name: 'site'});
        } else if(currentUrl.includes('/transmissionLine')) {
            this.toggleMenu({name: 'transmissionLine'});
        } else if(currentUrl.includes('/plantLtsa')) {
            this.toggleMenu({name: 'plantLtsa'});
        } else if(currentUrl.includes('/unitLtsa')) {
            this.toggleMenu({name: 'unitLtsa'});
        } else if(currentUrl.includes('/pipelineLtsa')) {
            this.toggleMenu({name: 'pipelineLtsa'});
        } else if(currentUrl.includes('/plant')) {
            this.toggleMenu({name: 'plant'});
        } else if(currentUrl.includes('/pipeline')) {
            this.toggleMenu({name: 'pipeline'});
        } else if(currentUrl.includes('/confirmedProject')) {
            this.toggleMenu({name: 'confirmedProject'});
        } else if(currentUrl.includes('/unconfirmedProject')) {
            this.toggleMenu({name: 'unconfirmedProject'});
        } else if(currentUrl.includes('/unit')) {
            this.toggleMenu({name: 'unit'});
        } else if(currentUrl.includes('/tank')) {
            this.toggleMenu({name: 'tank'});
        } else if(currentUrl.includes('/drive')) {
            this.toggleMenu({name: 'drive'});
        } else if(currentUrl.includes('/otherEquipment')) {
            this.toggleMenu({name: 'otherEquipment'});  
        } else if(currentUrl.includes('/miningEquipment')) {
            this.toggleMenu({name: 'miningEquipment'});    
        } else if(currentUrl.includes('/umbrellaProject')) {
            this.toggleMenu({name: 'umbrellaProject'});
        } else if(currentUrl.includes('/area')) {
            this.toggleMenu({name: 'area'});
        } else if(currentUrl.includes('/boiler')) {
            this.toggleMenu({name: 'boiler'});
        } else if(currentUrl.includes('/release')) {
            this.toggleMenu({name: 'release'});
        } else if(currentUrl.includes('/offlineEvent')) {
            this.toggleMenu({name: 'offlineEvent'});
        } else if(currentUrl.includes('/geography')) {
            this.toggleMenu({name: 'geography'});
        } else if(currentUrl.includes('/regionAlerting')) {
            this.toggleMenu({name: 'regionAlerting'});
        } else if(currentUrl.includes('/subscription/edit')) {
            this.toggleMenu({name: 'subscriptionEdit'});
        } else if(currentUrl.includes('/subscription/copy')) {
            this.toggleMenu({name: 'subscriptionCopy'});
        } else {
            this.toggleCodeFilesMenus(currentUrl);
        }
        this.asideService.changeMenuState();

   }

    private toggleCodeFilesMenus(currentUrl: string): void {
        const entityNames = [EntityName.AEC_FIRM, EntityName.MANUFACTURER, EntityName.SERV_CONTRACTOR, 
            EntityName.CRUDE_SLATE_TYPE, EntityName.KV_RATING, EntityName.PIPELINE_SYSTEM, EntityName.DRIVEN_EQUIPMENT_TYPE, 
            EntityName.HS_PRODUCT, EntityName.TRANSMISSION_SYSTEM, EntityName.EQUIPMENT_TYPE, EntityName.PIPELINE_METER,
            EntityName.SIC_PRODUCT];
        entityNames.some(entityName => {
            const entityUrl = ObjectUtils.getEntityPath(entityName);
            if(currentUrl.includes('/'+entityUrl)) {
                this.toggleMenuByEntityName({name: entityUrl}, entityName, this.codeFileItems, false, false);
                return true;
            }
        })
    }
    
    private toggleMenuByEntityName(option:IMenu, entityName: EntityName, items: MenuItem[], showPDFLink: boolean, showClientView: boolean = true): void {
        const entityUrl = ObjectUtils.getEntityPath(entityName);
        if(option.name === entityUrl) {
            let title: string = this.headerService.getEntityTitle(entityName);
            items[0].label = title + ' Input';
            this.items = this.getVisibleMenuItems(items, option);
            this.headerService.changeHeader(title, true, entityName, {showClientView: showClientView, showPDFLink: showPDFLink});
        }
        
        this.addItemsActions(this.items, option);
    }

    private getMenuItem(entityName: EntityName, visible: boolean, submenu: boolean, label: string = ''): any {
        return {
            label: StringUtils.isEmpty(label) ? entityName : label,
            command: (click: any) => {this.goTo(ObjectUtils.getEntityPath(entityName), this.headerService.getEntityTitle(entityName))},
            styleClass: submenu ? 'submenu-item' : 'padding-l',
            visible: visible
        };
    }
   
    private toggleMenu(option:IMenu) {
        if(option.name === 'company') {
            this.items = this.companyItems;
            this.headerService.changeHeader(EntityName.COMPANY, true, EntityName.COMPANY, {showClientView:true, showQueryWithSignal: true});
        } else if (option.name === 'home') {
            this.items = this.getVisibleMenuItems(this.home, option);
            this.headerService.clearReleaseSection();
        } else if( option.name === 'preferences' ) {
            this.items = this.getVisibleMenuItems(this.preferencesItems, option);
            this.headerService.changeHeader(EntityName.PREFERENCES, true, EntityName.PREFERENCES, {showClientView:false}, '', null, () => false);
        } else if( option.name === 'site' ) {
            this.items = this.getVisibleMenuItems(this.siteItems, option);
            this.headerService.changeHeader(EntityName.SITE, true, EntityName.SITE, {showClientView:false, showQueryWithSignal: true});
        } else if( option.name === 'pipeline' ) {
            this.items = this.getVisibleMenuItems(this.pipelineItems, option);
            this.headerService.changeHeader(EntityName.ASSET_PIPELINE, true, EntityName.ASSET_PIPELINE, {showClientView:true, showQueryWithSignal: true});
        } else if( option.name === 'transmissionLine' ) {
            this.items = this.getVisibleMenuItems(this.transmissionLineItems, option);
            this.headerService.changeHeader(EntityName.ASSET_TRANSMISSION_LINE, true, EntityName.ASSET_TRANSMISSION_LINE, {showClientView:true, showQueryWithSignal: true});
        } else if( option.name === 'plant' ) {
            this.items = this.getVisibleMenuItems(this.plantItems, option);
            this.headerService.changeHeader(EntityName.PLANT, true, EntityName.PLANT, {showClientView:true, showQueryWithSignal: true});
        } else if( option.name === 'plantLtsa' ) {
            this.items = this.getVisibleMenuItems(this.plantLtsaItems, option);
            this.headerService.changeHeader(EntityName.PLANT_LTSA, true, EntityName.PLANT_LTSA, {showClientView:true});
        } else if( option.name === 'unitLtsa' ) {
            this.items = this.getVisibleMenuItems(this.unitLtsaItems, option);
            this.headerService.changeHeader(EntityName.UNIT_LTSA, true, EntityName.UNIT_LTSA, {showClientView:true});
        } else if( option.name === 'pipelineLtsa' ) {
            this.items = this.getVisibleMenuItems(this.pipelineLtsaItems, option);
            this.headerService.changeHeader(EntityName.PIPELINE_LTSA, true, EntityName.PIPELINE_LTSA, {showClientView:false});
        } else if( option.name === 'confirmedProject' ) {
            this.items = this.getVisibleMenuItems(this.confirmedProjectItems, option);
            this.headerService.changeHeader(EntityName.PROJECT_CONFIRMED, true, EntityName.PROJECT_CONFIRMED, {showClientView:true, showQueryWithSignal: true});
        } else if( option.name === 'unconfirmedProject' ) {
            this.items = this.getVisibleMenuItems(this.unconfirmedProjectItems, option);
            this.headerService.changeHeader(EntityName.PROJECT_UNCONFIRMED, true, EntityName.PROJECT_UNCONFIRMED, {showClientView:true, showQueryWithSignal: true});
        } else if( option.name === 'unit' ) {
            this.items = this.getVisibleMenuItems(this.unitItems, option);
            this.headerService.changeHeader(EntityName.UNIT, true, EntityName.UNIT, {showClientView:true, showQueryWithSignal: true});
        } else if( option.name === 'tank' ) {
            this.items = this.getVisibleMenuItems(this.tankItems, option);
            this.headerService.changeHeader(EntityName.TANK, true, EntityName.TANK, {showClientView:false, showQueryWithSignal: true});
        } else if( option.name === 'drive' ) {
            this.items = this.getVisibleMenuItems(this.driveItems, option);
            this.headerService.changeHeader(EntityName.TURBINE, true, EntityName.TURBINE, {showClientView:true, showQueryWithSignal: true});
        } else if( option.name === 'otherEquipment' ) {
            this.items = this.getVisibleMenuItems(this.equipmentItems, option);
            this.headerService.changeHeader(EntityName.OEQUIPMENT, true, EntityName.OEQUIPMENT, {showClientView:true, showQueryWithSignal: true});                                               
        } else if( option.name === 'miningEquipment' ) {
            this.items = this.getVisibleMenuItems(this.mEquipmentItems, option);
            this.headerService.changeHeader(EntityName.MEQUIPMENT, true, EntityName.MEQUIPMENT, {showClientView:true, showQueryWithSignal: true});                                                
        } else if( option.name === 'umbrellaProject' ) {
            this.items = this.getVisibleMenuItems(this.umbrellaProjectItems, option);
            this.headerService.changeHeader(EntityName.UMBRELLA_PROJECT, true, EntityName.UMBRELLA_PROJECT, { showClientView:false, showPDFLink: false });
        } else if( option.name === 'area' ) {
            this.items = this.getVisibleMenuItems(this.areaItems, option);
            this.headerService.changeHeader(EntityName.AREA, true, EntityName.AREA, {showClientView:true, showQueryWithSignal: true});
        } else if( option.name === 'boiler' ) {
            this.items = this.getVisibleMenuItems(this.boilerItems, option);
            this.headerService.changeHeader(EntityName.BOILER, true, EntityName.BOILER, {showClientView:true, showQueryWithSignal: true});
        } else if( option.name === 'release' ) {
            this.items = this.getVisibleMenuItems(this.releaseItems, option);
        } else if( option.name === 'offlineEvent' ) {
            this.items = this.getVisibleMenuItems(this.offlineEventItems, option);
            this.headerService.changeHeader(EntityName.OFFLINE_EVENT, true, EntityName.OFFLINE_EVENT, {showClientView:true, showQueryWithSignal: true});
        } else if( option.name === 'geography' ) {
            this.items = this.getVisibleMenuItems(this.geographyItems, option);
            this.headerService.changeHeader(EntityName.GEOGRAPHY, true, EntityName.GEOGRAPHY, {showClientView:false, showPDFLink: false, updateTitleOnChangeRelease: false});
        } else if( option.name === 'regionAlerting' ) {
            this.items = this.getVisibleMenuItems(this.regionAlertingItems, option);
            this.headerService.changeHeader(EntityName.REGION_ALERTING, true, EntityName.REGION_ALERTING, {showClientView:false});
        } else if( option.name === 'subscriptionEdit' ) {
            this.items = this.getVisibleMenuItems(this.subscriptionEditItems, option);
            this.headerService.changeHeader(EntityName.SUBSCRIPTION_EDIT, true, EntityName.SUBSCRIPTION_EDIT, {showClientView:false, showPDFLink: false, updateTitleOnChangeRelease: false});
        } else if( option.name === 'subscriptionCopy' ) {
            this.items = this.getVisibleMenuItems(this.subscriptionCopyItems, option);
            this.headerService.changeHeader(EntityName.SUBSCRIPTION_COPY, true, EntityName.SUBSCRIPTION_COPY, {showClientView:false, showPDFLink: false, updateTitleOnChangeRelease: false});
        }
        
        this.addItemsActions(this.items, option);
    }

    private addItemsActions(items: MenuItem[], option:IMenu) {
        if(option.menuItems != null && items != null && items.length > 0) {
            for (const menuItem of option.menuItems) {
                const sectionId = menuItem.id;
                if(sectionId) {
                   menuItem.id = `nav-section-${sectionId}`;
                   menuItem.command = (click) => {this.asideService.scrollToSection(sectionId)}
                } else {
                   console.error('Ignoring menu item with no "id" attribute', menuItem);
                }
            }
            items[0].items = option.menuItems;
        }
        this.applyFocusHighlight();
    }

    private goTo(path: string, target: string): void {
        globalThis.open(location.pathname + `#/ED/${path}`, StringUtils.safeWindowName(target));
    }

    private openClearLockUtility():void {
        const modalRef = this.dialogService.open(ClearLockUtilityComponent, {
            header: EntityName.CLEAR_LOCKS,
            width: '60rem',
            data: null,
            draggable: true,
            closable: true,
            keepInViewport: true,
            resizable: true,
            maximizable: true,
            styleClass: 'resizable-modal'
        })!;
        this.cellContainerService.registerDialog(modalRef);
    }

    private openDeveloperTools():void {
        this.dialogService.open(DeveloperToolsComponent, {
            header: EntityName.DEV_TOOLS,
            width: '60rem',
            data: null,
            draggable: true,
            closable: true,
            keepInViewport: true,
            resizable: true,
            maximizable: true,
            styleClass: 'resizable-modal'
        });
    }

    private getVisibleMenuItems(menu: Array<MenuItem>, option: IMenu): Array<MenuItem> {
        if(menu.length === 1) {
            const firstMenu = menu[0]!;
            if(option.changeVisibility){
                const changeVisibility = option.changeVisibility;
                const childMenuItem = firstMenu.items?.find(i => i.label === changeVisibility.label);
                if(childMenuItem) {
                    childMenuItem.visible = changeVisibility.visible;
                }
            }
            if(option.changeVisibilities){
                option.changeVisibilities.forEach(iMenu =>{
                    const childMenuItem = firstMenu.items?.find(i => i.label === iMenu.label);
                    if(childMenuItem) {
                        childMenuItem.visible = iMenu.visible;
                    }
                });
            }
            const menuClone = {...firstMenu, items: firstMenu.items?.filter(i => i.visible)};
            return [menuClone];
        }

        return menu;
    }
    
    /**
     * Builds a MenuItem that scrolls to a `general-container` section on click and carries a section id 
     * that can optionally be used by the focus-highlight logic
     */
    private buildMenuItem(label: string, sectionId: string, visible = true): MenuItem {
        const item: MenuItem = {
            id: `nav-section-${sectionId}`,
            label,
            visible,
            command: () => this.asideService.scrollToSection(sectionId)
        };
        (item as any)[NavGroupItemComponent.SECTION_ID_KEY] = sectionId;
        return item;
    }  
    
    /**
     * Toggles the focus-highlight class on the leaf whose section id matches the AsideService's current focused section.
     * It runs on every focus change plus once at the end of toggleMenu(), but is idempotent and cheap.
     */
    private applyFocusHighlight(): void {
        const HIGHLIGHT = NavGroupItemComponent.FOCUS_HIGHLIGHT_CLASS;
        const targetId = this.currentFocusedSectionId;

        // Defer to a microtask so PrimeNG has flushed any pending menu render before we query the DOM.
        // Without this, the first highlight after a menu swap can miss because the anchors don't exist yet.
        queueMicrotask(() => {
            const host = this.hostRef.nativeElement;

            // Clear the highlight from anything currently wearing it. Scoped to
            // this component's host so we don't touch other aside instances.
            host.querySelectorAll<HTMLElement>('.' + HIGHLIGHT).forEach(el => el.classList.remove(HIGHLIGHT));

            if (!targetId) {
                return;
            }

            // Apply to the single anchor whose DOM id matches the focused section.
            const target = host.querySelector<HTMLElement>(`#nav-section-${CSS.escape(targetId)}`);
            target?.classList.add(HIGHLIGHT);
        });
    }    
}
